import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { format } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ITraining } from '../../../types/interfaces/training';
import { formatTrainingCalories } from '../../../utils/training';
import { useStyles } from './AppTrainingCaloriesBarChart.styles';

export interface IChartDataPoint {
  date: string;
  totalCalories: number;
  trainingTitle: string;
}

export interface IAppTrainingCaloriesBarChartProps {
  trainings: ITraining[];
  height?: number;
}

export default function AppTrainingCaloriesBarChart({
  trainings,
  height = 400,
}: IAppTrainingCaloriesBarChartProps) {
  const theme = useTheme();

  const { classes } = useStyles();
  const { t } = useTranslation();

  const chartData = useMemo<IChartDataPoint[]>(() => {
    if (!trainings || trainings.length === 0) {
      return [];
    }

    const sortedTrainings = [...trainings]
      .filter((training) => training.startDate && training.totalCalories)
      .sort((a, b) => {
        if (!a.startDate || !b.startDate) return 0;
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateA.getTime() - dateB.getTime();
      });

    return sortedTrainings.map((training) => ({
      date: format(new Date(training.startDate!), 'dd.MM.yyyy'),
      totalCalories: training.totalCalories || 0,
      trainingTitle: training.title,
    }));
  }, [trainings]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Box className={classes.wrapper}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => formatTrainingCalories(value)}
            label={{
              value: t('trainings.totalCalories') || 'Burned calories',
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload as IChartDataPoint;
                return (
                  <Box
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      padding: theme.spacing(1.5),
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.spacing(1),
                    }}>
                    <Box sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                      {data.trainingTitle}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {t('trainings.trainingDate')}: {data.date}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', marginTop: 0.5 }}>
                      {t('trainings.totalCalories')}:{' '}
                      {formatTrainingCalories(data.totalCalories)}
                    </Box>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Bar
            dataKey="totalCalories"
            fill={theme.palette.primary.main}
            name={t('trainings.totalCalories') || 'Burned calories'}
            radius={[4, 4, 0, 0]}
            activeBar={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
